/**
 * Config Boundary - Type-Safe Config Transformations
 *
 * This module is the SINGLE SOURCE OF TRUTH for all config transformations.
 * It ensures complete type safety from:
 *   Database → Backend → API → Frontend → Export → Import → API → Backend → Database
 *
 * DESIGN PRINCIPLES:
 * 1. All types are derived from Zod schemas (no manual type definitions)
 * 2. Every transformation has compile-time verification
 * 3. Every transformation has runtime validation
 * 4. NO FALLBACKS - missing data fails loudly
 * 5. All field mappings are explicit and verified
 */

import { isValidTimezone } from "@bavard/utils";
import { z } from "zod";
import { TOOL_NAMES } from "../ai/tool-contracts";
import {
	AgentMediaCapabilitiesSchema,
	DEFAULT_AGENT_MEDIA_CAPABILITIES,
} from "./agent-media-capabilities";
import { EventTypeDefinitionSchema } from "./event-type-definition";
import {
	NotificationChannelSchema,
	NotificationRuleOptionsSchema,
} from "./notification-rules";

// ============================================================================
// STRICT ENUM VALUES - DERIVED FROM CONSTANTS (SINGLE SOURCE OF TRUTH)
// ============================================================================

/**
 * Valid calendar view values - must match CALENDAR_VIEW_OPTIONS
 * Using literal tuple for strict typing with z.enum()
 */
export const CALENDAR_VIEW_VALUES = [
	"timeGridWeek",
	"timeGridDay",
	"dayGridMonth",
	"listMonth",
	"multiMonthYear",
] as const;

// ============================================================================
// ENUM VALUE CONSTANTS - EXPORTED FOR FRONTEND TYPE SAFETY
// ============================================================================

/**
 * Valid reasoning effort values for LLM settings.
 * Maps 1:1 to OpenRouter reasoning effort levels:
 * - 'off': Disable reasoning entirely (no thinking tokens)
 * - 'low': Light reasoning for simple tasks
 * - 'medium': Balanced reasoning for most work
 * - 'high': Deep reasoning for complex tasks
 */
export const LLM_REASONING_EFFORT_VALUES = [
	"off",
	"low",
	"medium",
	"high",
] as const;
export type LlmReasoningEffort = (typeof LLM_REASONING_EFFORT_VALUES)[number];

/**
 * Valid verbosity values for LLM settings
 */
export const LLM_VERBOSITY_VALUES = ["low", "medium", "high"] as const;
export type LlmVerbosity = (typeof LLM_VERBOSITY_VALUES)[number];

/**
 * Valid calendar direction values
 */
export const CALENDAR_DIRECTION_VALUES = ["ltr", "rtl", "auto"] as const;
export type CalendarDirection = (typeof CALENDAR_DIRECTION_VALUES)[number];

/**
 * Valid calendar systems for calendar rendering.
 */
export const CALENDAR_SYSTEM_VALUES = ["gregorian", "hijri"] as const;
export type CalendarSystem = (typeof CALENDAR_SYSTEM_VALUES)[number];

/**
 * Valid time format values
 */
export const TIME_FORMAT_VALUES = ["12h", "24h", "hidden"] as const;
export type TimeFormat = (typeof TIME_FORMAT_VALUES)[number];

/**
 * Valid column data type values
 */
export const COLUMN_DATA_TYPE_VALUES = [
	"text",
	"number",
	"datetime",
	"phone",
	"name",
	"dropdown",
	"boolean",
] as const;
export type ColumnDataType = (typeof COLUMN_DATA_TYPE_VALUES)[number];

/**
 * Valid column editor mode values
 */
export const COLUMN_EDITOR_MODE_VALUES = [
	"input",
	"dropdown",
	"wheel",
	"checkbox",
] as const;
export type ColumnEditorMode = (typeof COLUMN_EDITOR_MODE_VALUES)[number];

/**
 * Valid tool category values
 * Note: The `ToolCategory` type is exported from contracts/tools.contract.ts (identical definition)
 */
export const TOOL_CATEGORY_VALUES = [
	"shared",
	"assistant",
	"system-agent",
] as const;

// ============================================================================
// ZOD SCHEMAS - SINGLE SOURCE OF TRUTH FOR CONFIG TYPES
// ============================================================================

/**
 * Working hours schema
 */
export const WorkingHoursSchema = z.object({
	startTime: z.string(),
	endTime: z.string(),
});
export type WorkingHours = z.infer<typeof WorkingHoursSchema>;

/**
 * Day-specific hours schema
 * Backend uses 'day', frontend transforms to/from 'dayOfWeek' at the adapter layer.
 *
 * A weekday may appear in MULTIPLE entries (split shifts) — each entry is one
 * working range. Ranges for the same day must not overlap (see
 * `DaySpecificHoursArraySchema`).
 */
export const DaySpecificHoursSchema = z.object({
	day: z.number().min(0).max(6),
	startTime: z.string(),
	endTime: z.string(),
});
export type DaySpecificHours = z.infer<typeof DaySpecificHoursSchema>;

/** Matches zero-padded HH:MM strings — the shape produced by the time inputs. */
const HH_MM_REGEX = /^\d{2}:\d{2}$/;

/**
 * Per-day overlap check shared by the top-level day-specific hours and each
 * custom range's day-specific hours. Only well-formed entries (HH:MM with
 * startTime < endTime) participate — malformed legacy values are ignored here
 * and dropped by the window resolvers at read time, so this refinement can
 * never reject data that was valid before multi-range support (one row per
 * day cannot overlap). Touching boundaries are allowed: end is exclusive, so
 * 09:00–13:00 followed by 13:00–17:00 is valid.
 */
const addPerDayOverlapIssues = (
	entries: readonly { day: number; endTime: string; startTime: string }[],
	ctx: z.RefinementCtx
): void => {
	const byDay = new Map<number, { startTime: string; endTime: string }[]>();
	for (const entry of entries) {
		if (
			!(
				HH_MM_REGEX.test(entry.startTime) &&
				HH_MM_REGEX.test(entry.endTime) &&
				entry.startTime < entry.endTime
			)
		) {
			continue;
		}
		const list = byDay.get(entry.day) ?? [];
		list.push(entry);
		byDay.set(entry.day, list);
	}
	for (const [day, ranges] of byDay) {
		const sorted = [...ranges].sort((a, b) =>
			a.startTime.localeCompare(b.startTime)
		);
		for (let i = 1; i < sorted.length; i++) {
			const prev = sorted[i - 1];
			const curr = sorted[i];
			if (prev && curr && curr.startTime < prev.endTime) {
				ctx.addIssue({
					code: "custom",
					message: `Day ${day} has overlapping working-hour ranges (${prev.startTime}–${prev.endTime} and ${curr.startTime}–${curr.endTime})`,
				});
			}
		}
	}
};

/** Array schema for day-specific hours with per-day overlap validation. */
export const DaySpecificHoursArraySchema = z
	.array(DaySpecificHoursSchema)
	.superRefine(addPerDayOverlapIssues);

/**
 * Day-specific slot duration schema
 * - dayOfWeek: 0-6 (Sunday-Saturday)
 * - durationMinutes: slot duration in minutes for this day
 * - id: unique identifier for this entry
 * - slotDurationHours: computed value (durationMinutes / 60) for display
 */
export const DaySpecificSlotDurationSchema = z.object({
	id: z.string().nullish(),
	dayOfWeek: z.number().min(0).max(6),
	durationMinutes: z.number().positive(),
	slotDurationHours: z.number().positive().nullish(),
});
export type DaySpecificSlotDuration = z.infer<
	typeof DaySpecificSlotDurationSchema
>;

/**
 * Range-level day-specific hours schema
 * Extends DaySpecificHoursSchema with an optional slot duration override per day.
 * - slotDurationHours: null means inherit from the range's slotDurationHours (or global)
 */
export const RangeDaySpecificHoursSchema = DaySpecificHoursSchema.extend({
	slotDurationHours: z.number().positive().nullable(),
});
export type RangeDaySpecificHours = z.infer<typeof RangeDaySpecificHoursSchema>;

/**
 * Custom calendar range schema
 * - id: unique identifier for this range
 * - name: display name for the range
 * - startDate: YYYY-MM-DD format start date
 * - endDate: YYYY-MM-DD format end date
 * - startTime: HH:mm format start time
 * - endTime: HH:mm format end time
 * - workingDays: array of day numbers (0-6, Sunday-Saturday)
 * - slotDurationHours: optional slot duration override for this range
 * - daySpecificHours: per-day hour + slot duration overrides within this range
 */
export const customCalendarRangeSchema = z.object({
	id: z.string(),
	name: z.string(),
	startDate: z.string(),
	endDate: z.string(),
	startTime: z.string(),
	endTime: z.string(),
	workingDays: z.array(z.number().min(0).max(6)),
	slotDurationHours: z.number().positive().nullable(),
	// A weekday may carry several entries (split shifts) — same overlap rule
	// as the top-level day-specific hours.
	daySpecificHours: z
		.array(RangeDaySpecificHoursSchema)
		.superRefine(addPerDayOverlapIssues),
});
export type CustomCalendarRange = z.infer<typeof customCalendarRangeSchema>;
// Alias for internal use
const CustomCalendarRangeSchema = customCalendarRangeSchema;

export const CustomCalendarRangesSchema = z
	.array(CustomCalendarRangeSchema)
	.superRefine((ranges, ctx) => {
		const ordered = ranges
			.map((range, index) => ({ range, index }))
			.sort(
				(left, right) =>
					left.range.startDate.localeCompare(right.range.startDate) ||
					left.range.endDate.localeCompare(right.range.endDate)
			);
		for (let index = 1; index < ordered.length; index += 1) {
			const previous = ordered[index - 1];
			const current = ordered[index];
			if (
				previous &&
				current &&
				current.range.startDate <= previous.range.endDate
			) {
				ctx.addIssue({
					code: "custom",
					message: `Date range overlaps "${previous.range.name}"`,
					path: [current.index, "startDate"],
				});
			}
		}
	});

/**
 * Rich dropdown option metadata for a calendar/documents column.
 *
 * The legacy `metadata.options: string[]` form only carried the option *value*
 * (label was derived as label === value on the frontend and i18n was lost on
 * export). This richer form lets a column round-trip a localized display label
 * and/or an i18n key alongside the stored value.
 * - value:   the stored/serialized option value (required)
 * - label:   the human-readable display label (may differ from `value`)
 * - i18nKey: a translation key resolved by the frontend at render time
 */
export const CalendarColumnOptionSchema = z.object({
	value: z.string(),
	label: z.string().nullish(),
	i18nKey: z.string().nullish(),
});
export type CalendarColumnOption = z.infer<typeof CalendarColumnOptionSchema>;

/**
 * Numeric range metadata for a `number`/`wheel` column. Carries the min/max
 * bounds and the step used by the wheel/number editor so an export→import
 * roundtrip preserves the configured range instead of nulling it.
 */
export const CalendarColumnNumericRangeSchema = z.object({
	min: z.number().nullish(),
	max: z.number().nullish(),
	step: z.number().nullish(),
});
export type CalendarColumnNumericRange = z.infer<
	typeof CalendarColumnNumericRangeSchema
>;

/**
 * Calendar column schema
 */
export const CalendarColumnSchema = z.object({
	id: z.string(),
	name: z.string(),
	title: z.string(),
	dataType: z.enum(COLUMN_DATA_TYPE_VALUES),
	width: z.number(),
	isEditable: z.boolean(),
	isRequired: z.boolean(),
	editorMode: z.enum(COLUMN_EDITOR_MODE_VALUES),
	databaseField: z.string(),
	metadata: z
		.object({
			// Legacy value-only option list. Kept for back-compat; when present
			// alongside `optionsMeta`, `optionsMeta` is the authoritative source.
			options: z.array(z.string()).nullish(),
			// Rich dropdown options carrying label + i18nKey so localized labels
			// survive an export→import roundtrip (was collapsing label === value).
			optionsMeta: z.array(CalendarColumnOptionSchema).nullish(),
			// Numeric column min/max/step (was being nulled on roundtrip).
			numericRange: CalendarColumnNumericRangeSchema.nullish(),
			useWheel: z.boolean().nullish(),
			// `name` columns only: minimum words for a valid person name
			// (2 = first + last, 3 = first + middle + last). Absent → 2.
			nameMinWords: z.number().int().min(2).max(3).nullish(),
		})
		.nullish(),
});
export type CalendarColumnType = z.infer<typeof CalendarColumnSchema>;

/**
 * Event time format schema
 */
export const eventTimeFormatSchema = z.object({
	format: z.enum(TIME_FORMAT_VALUES),
	showMinutes: z.boolean(),
	showMeridiem: z.boolean(),
});
export type EventTimeFormat = z.infer<typeof eventTimeFormatSchema>;
// Alias for internal use
const EventTimeFormatSchema = eventTimeFormatSchema;

/**
 * Notification preferences schema
 */
export const NotificationPreferencesSchema = z.object({
	remindersEnabled: z.boolean(),
	notifyOnCancellation: z.boolean(),
	timezone: z
		.string()
		.refine(isValidTimezone, "Invalid IANA timezone identifier"),
});
export type NotificationPreferences = z.infer<
	typeof NotificationPreferencesSchema
>;

/**
 * Event colors schema (org-wide defaults only — per-EventType colors live on
 * the EventType row, see `contracts/event-types.contract.ts`).
 */
export const EventColorsSchema = z.object({
	defaultEventColor: z.string(),
	documentStrokeColor: z.string(),
});
export type EventColorsType = z.infer<typeof EventColorsSchema>;

/**
 * Tool configuration schema
 * Uses enum for name to ensure only valid tool names are accepted (contract-first)
 *
 * `language` is an optional per-tool override (currently used by `saveFeedback`
 * for the operator's preferred summary language). When omitted, the server
 * falls back to the language derived from `defaultCountryPrefix`. Stored as a
 * raw string (not the LocaleCode enum) so adding a new locale doesn't require
 * a config-schema migration; the LLM tolerates any IETF language tag.
 */
export const ToolConfigSchema = z.object({
	name: z.enum(TOOL_NAMES),
	enabled: z.boolean(),
	category: z.enum(TOOL_CATEGORY_VALUES),
	requiresApproval: z.boolean(),
	language: z.string().min(2).max(10).optional(),
});
export type ToolConfig = z.infer<typeof ToolConfigSchema>;

/**
 * Valid embedding strategy values
 * - 'standard': Direct embedding. Fast and cheap.
 * - 'contextual': LLM generates context prefix per chunk before embedding.
 *   More accurate retrieval but slower and costlier during ingestion.
 * - 'late': Jina late chunking — embed the window first, mean-pool per chunk.
 *   Strong on long/unstructured docs, zero LLM cost, provider-locked (Jina).
 */
export const EMBEDDING_STRATEGY_VALUES = [
	"standard",
	"contextual",
	"late",
] as const;
export type EmbeddingStrategy = (typeof EMBEDDING_STRATEGY_VALUES)[number];

/**
 * Valid reranking mode values
 * - 'off': Results ranked by hybrid search score only.
 * - 'on': Cross-encoder re-ranks results for higher relevance. Adds ~200ms latency per search.
 * - 'automatic': System decides based on query complexity.
 */
export const RERANKING_MODE_VALUES = ["off", "on", "automatic"] as const;
export type RerankingMode = (typeof RERANKING_MODE_VALUES)[number];

/**
 * RAG settings schema
 *
 * NOTE: searchThreshold has no .default() to prevent Zod from injecting
 * values during partial updates. Defaults are handled explicitly where the
 * config is assembled (the server's default-config builder and frontend
 * form factories).
 */
export const WebSourceSchema = z.object({
	url: z.string().url(),
	crawlDepth: z.number().min(1).max(100),
	schedule: z.string().optional(), // cron expression, e.g., "0 0 * * *" for daily
	enabled: z.boolean(),
});
export type WebSource = z.infer<typeof WebSourceSchema>;

/**
 * How ONE document's images are handled (D4). Set per document on
 * `RagDocument.documentMeta.imagePolicy`; `ragSettings.imagePolicy` is the ORG
 * DEFAULT a document inherits when it carries no override.
 *
 * - 'describe' (default) — a vision model describes each figure and the text is
 *   spliced into the chunked content. INGEST-TIME.
 * - 'attach' — no ingest-time text; the crop's bytes are attached to the model's
 *   context per turn (vision-gated). ANSWER-TIME.
 * - 'ocr' — the figure's verbatim text is extracted and spliced. INGEST-TIME.
 * - 'ignore' — no description, no OCR, no stored crop. INGEST-TIME.
 *
 * The three INGEST-TIME modes change the text baked into the chunks, so a
 * change only takes effect when the document is reprocessed.
 *
 * Structural mirror of the server's `RagImagePolicySchema`
 * (`server/src/domain/schemas/rag/figure-meta.schema.ts`).
 */
export const RAG_IMAGE_POLICY_VALUES = [
	"describe",
	"attach",
	"ocr",
	"ignore",
] as const;
export type RagImagePolicy = (typeof RAG_IMAGE_POLICY_VALUES)[number];

export const RagSettingsSchema = z.object({
	embeddingModel: z.string().optional(),
	searchThreshold: z.number().min(0).max(1),
	embeddingStrategy: z.enum(EMBEDDING_STRATEGY_VALUES).optional(),
	rerankingMode: z.enum(RERANKING_MODE_VALUES).optional(),
	webSources: z.array(WebSourceSchema).optional(),
	// WS10.6 — "ready-made queries": expose saved parameterized queries to the
	// agent (runSavedQuery). `savedQueriesOnly` is the lockdown that withholds
	// free-form structured SQL (queryStructuredData) once ready-made queries are
	// on. Optional so partial config updates and the api-client mirror unify.
	savedQueriesEnabled: z.boolean().optional(),
	savedQueriesOnly: z.boolean().optional(),
	// Scheduled re-crawl of URL sources (the honest "auto refresh"; standard+
	// premium plans). `intervalHours` is user-selectable daily (24) or weekly
	// (168). Defaulted (not optional) so a parsed config always carries a value
	// for the sweep due-ness predicate and the FE toggle. Plan §4.3.
	autoRefresh: z
		.object({
			enabled: z.boolean(),
			intervalHours: z.union([z.literal(24), z.literal(168)]),
		})
		.default({ enabled: false, intervalHours: 168 }),
	// D4 — org default for per-document image handling. Optional (not defaulted)
	// so a partial config update never injects a value the user did not send;
	// the DB column is non-null with a 'describe' default, so a loaded config
	// always carries one.
	imagePolicy: z.enum(RAG_IMAGE_POLICY_VALUES).optional(),
});
export type RagSettings = z.infer<typeof RagSettingsSchema>;

/**
 * Countries the WS12 sensitive-data detector supports for national-ID masking
 * (formats are country-anchored — see the server's sensitive-data-detector).
 */
export const NATIONAL_ID_COUNTRY_VALUES = ["SA", "EG"] as const;
export type NationalIdCountry = (typeof NATIONAL_ID_COUNTRY_VALUES)[number];

/**
 * WS12 follow-up — per-org sensitive-data egress guard (masking) settings.
 * Cards/IBANs are checksum-validated and masked by default; national-ID
 * masking is opt-in and only effective once a country anchors the format.
 */
export const SensitiveDataGuardSchema = z.object({
	maskCards: z.boolean(),
	maskIbans: z.boolean(),
	maskNationalIds: z.boolean(),
	nationalIdCountry: z.enum(NATIONAL_ID_COUNTRY_VALUES).nullable(),
});
export type SensitiveDataGuard = z.infer<typeof SensitiveDataGuardSchema>;

export const DEFAULT_SENSITIVE_DATA_GUARD: SensitiveDataGuard = {
	maskCards: true,
	maskIbans: true,
	maskNationalIds: false,
	nationalIdCountry: null,
};

/**
 * Valid multi-agent RAG answering modes (the /config "AI agents" slider stops).
 * - '1': single agent (NotebookLM-style) — the default, unchanged production
 *   path. Marked tool results + citation finalize; no extra model calls.
 * - '4': evidence-first — a researcher subagent (its own model) runs the RAG
 *   tools and returns a curated evidence pack; the main agent answers from it.
 * - '5': + verifier loop — a verifier grades every claim's citation
 *   faithfulness; unresolved problems route back (retrieve_more → researcher,
 *   rewrite/remove → main) up to `verifierMaxLoops` times.
 *
 * Stored server-side as the `RagAgentMode` enum (SINGLE/RESEARCHER/VERIFIER);
 * the numeric wire form keeps the slider semantics stable.
 */
export const RAG_AGENT_MODE_VALUES = ["1", "4", "5"] as const;
export type RagAgentMode = (typeof RAG_AGENT_MODE_VALUES)[number];

/** Default mode-5 verifier loop cap. */
export const DEFAULT_VERIFIER_MAX_LOOPS = 2;
/** Recommended utility-tier verifier model (cheap, structured output). */
export const DEFAULT_VERIFIER_MODEL = "google/gemini-3.1-flash-lite";

/**
 * Org-defined system-prompt preset (guided setup wizard + /config
 * ai-assistant tab). Structural copy of the domain schema in
 * `server/src/domain/entities/app-config/ai-prompt-preset.ts` — keep in sync.
 */
export const AiPromptPresetSchema = z.object({
	id: z.string().min(1).max(64),
	name: z.string().min(1).max(80),
	content: z.string().min(1).max(20_000),
});
export type AiPromptPreset = z.infer<typeof AiPromptPresetSchema>;

/**
 * Per-preset limits, exported so the /config form can enforce them AT THE INPUT
 * rather than letting the user compose a preset the wire schema will reject.
 * Mirrors the `.max(...)` bounds above (and the domain's constants).
 */
export const MAX_AI_PROMPT_PRESET_NAME_LENGTH = 80;
export const MAX_AI_PROMPT_PRESET_CONTENT_LENGTH = 20_000;
/** Max presets an org may store (mirrors the wire schema's `.max(50)`). */
export const MAX_AI_PROMPT_PRESETS = 50;

/**
 * App config schema - the complete configuration object
 */
export const AppConfigSchema = z.object({
	// Working schedule
	workingDays: z.array(z.number().min(0).max(6)),
	defaultWorkingHours: WorkingHoursSchema,
	daySpecificHours: DaySpecificHoursArraySchema,
	slotDurationHours: z.number().positive(),
	daySpecificSlotDurations: z.array(DaySpecificSlotDurationSchema),
	customCalendarRanges: CustomCalendarRangesSchema,
	// Most bookings of event types WITHOUT a resource the calendar holds at one
	// instant, across all of them; `null` = no limit. A type on a resource counts
	// against that resource's occupancy instead. `.default(null)` keeps configs
	// assembled before the field existed valid.
	calendarMaxOccupancy: z.number().int().min(1).nullable().default(null),

	// Grid columns
	calendarColumns: z.array(CalendarColumnSchema),
	documentsColumns: z.array(CalendarColumnSchema),

	// Locale & region
	defaultCountryPrefix: z.string(),
	availableLanguages: z.array(z.string()),
	availableCalendarViews: z.array(z.enum(CALENDAR_VIEW_VALUES)),
	timezone: z
		.string()
		.refine(isValidTimezone, "Invalid IANA timezone identifier"),

	// LLM settings
	llmProvider: z.string(),
	llmModel: z.string().nullable(),
	llmReasoningEffort: z.enum(LLM_REASONING_EFFORT_VALUES),
	llmVerbosity: z.enum(LLM_VERBOSITY_VALUES),
	llmModelFallback: z.string().nullable(),
	llmFallbackReasoningEffort: z.enum(LLM_REASONING_EFFORT_VALUES),
	llmFallbackVerbosity: z.enum(LLM_VERBOSITY_VALUES),
	llmFallbackMaxOutputTokens: z.number().nullable(),
	streamReasoning: z.boolean(),
	agentMediaCapabilities: AgentMediaCapabilitiesSchema.default(
		DEFAULT_AGENT_MEDIA_CAPABILITIES
	),
	// Independent send/receive media gates for both agents. Default true.
	aiAssistantReceiveImages: z.boolean().default(true),
	aiAssistantReceiveAudio: z.boolean().default(true),
	aiAssistantSendImages: z.boolean().default(true),
	aiAssistantSendAudio: z.boolean().default(true),
	systemAgentReceiveImages: z.boolean().default(true),
	systemAgentReceiveAudio: z.boolean().default(true),
	systemAgentSendImages: z.boolean().default(true),
	systemAgentSendAudio: z.boolean().default(true),
	/**
	 * Master gate for the operator-facing system agent. When false the sidebar
	 * hides the SYSTEM conversation, sends to it are refused, and no
	 * system-agent tools are built at runtime.
	 */
	systemAgentEnabled: z.boolean().default(true),
	/**
	 * Section master gates. Off = the whole tool family is withheld from the
	 * AI at runtime while the per-tool `tools` choices stay stored, so
	 * re-enabling restores the operator's setup.
	 */
	schedulingToolsEnabled: z.boolean().default(true),
	ragToolsEnabled: z.boolean().default(true),
	llmContextLimit: z.number().nullable(),
	llmMaxOutputTokens: z.number(),

	// Calendar display
	calendarFirstDay: z.number().min(0).max(6),
	eventTimeFormat: EventTimeFormatSchema,
	defaultCalendarView: z.string().nullable(),
	availableCalendarLocales: z.array(z.enum(CALENDAR_SYSTEM_VALUES)),
	calendarDirection: z.enum(CALENDAR_DIRECTION_VALUES),

	// Notifications
	notificationPreferences: NotificationPreferencesSchema,

	// Waitlist
	waitlistEnabled: z.boolean().default(false),
	waitlistOfferBatchSize: z.number().int().min(1).max(10).default(3),
	waitlistOfferTtlMinutes: z.number().int().min(5).max(1440).default(15),

	// Event types — denormalised lookup of the per-org EventType table.
	// Capacity / duration / multiple-future controls live on each EventType
	// row instead of as JSON sub-records on AppConfig (the deleted
	// `eventDurationSettings`, `slotCapacitySettings`, `eventCapacitySettings`).
	eventTypes: z.array(EventTypeDefinitionSchema).min(1),

	// Appearance
	eventColors: EventColorsSchema,

	// AI prompts
	systemPrompt: z.string(),
	systemAgentPrompt: z.string(),
	// Org-defined system-prompt presets (guided setup + ai-assistant tab).
	// `.default([])` keeps configs assembled before the field existed valid.
	aiPromptPresets: z.array(AiPromptPresetSchema).max(50).default([]),

	// Business info
	businessName: z.string().nullable(),
	businessAddress: z.string().nullable(),
	businessGoogleMapsUrl: z.url().nullable(),
	businessLandmark: z.string().nullable(),
	businessFloorUnit: z.string().nullable(),
	businessLatitude: z.number().nullable(),
	businessLongitude: z.number().nullable(),

	// AI tools configuration
	// Note: Invalid tool filtering should be done in the backend handler, not schema
	// This keeps type inference working correctly with .partial()
	tools: z.array(ToolConfigSchema),

	// RAG settings - REQUIRED (not optional)
	ragSettings: RagSettingsSchema,

	// Multi-agent RAG answering (modes 1/4/5).
	// `.default('1')` keeps the single-agent path the default on partial updates
	// and for any config assembled without the field.
	ragAgentMode: z.enum(RAG_AGENT_MODE_VALUES).default("1"),
	researcherModel: z.string().nullable().default(null),
	verifierModel: z.string().nullable().default(DEFAULT_VERIFIER_MODEL),
	verifierMaxLoops: z
		.number()
		.int()
		.min(1)
		.max(3)
		.default(DEFAULT_VERIFIER_MAX_LOOPS),
	// Org-configured model for the ingest document-summary (metadata) extractor.
	// null = the extractor uses its cheap built-in default.
	documentSummaryModel: z.string().nullable().default(null),

	// WS12 follow-up — per-org sensitive-data egress guard (masking) settings.
	// `.default(...)` keeps configs assembled before the field existed valid
	// (cards + IBANs masked, national IDs off until a country is chosen).
	sensitiveDataGuard: SensitiveDataGuardSchema.default(
		DEFAULT_SENSITIVE_DATA_GUARD
	),

	// Contact requirements for events
	requirePhoneForEvents: z.boolean().default(true),
	requireNameForEvents: z.boolean().default(true),

	// Reminder template configs (array of reminders, each with its own template + send time)
	reminderTemplateConfig: z.preprocess(
		(val) => {
			if (typeof val === "string" || val === undefined || val === null) {
				return [];
			}
			if (val && typeof val === "object" && !Array.isArray(val)) {
				const obj = val as Record<string, unknown>;
				return [
					{
						...obj,
						sendTime: obj.sendTime ?? "00:00",
						name: obj.name ?? "Reminder-1",
					},
				];
			}
			if (Array.isArray(val)) {
				return val.map((item, i) => {
					if (item && typeof item === "object") {
						const obj = item as Record<string, unknown>;
						return {
							...obj,
							sendTime: obj.sendTime ?? "00:00",
							name: obj.name ?? `Reminder-${i + 1}`,
						};
					}
					return item;
				});
			}
			return val;
		},
		z.array(
			z.object({
				name: z
					.string()
					.min(1)
					.max(50)
					.regex(/^[\w-]+$/),
				templateName: z.string(),
				language: z.string(),
				variableMapping: z.record(z.string(), z.string()),
				sendTime: z.string().regex(/^\d{2}:\d{2}$/),
				messageBody: z.string().optional(),
				// Delivery channels; DB defaults to WHATSAPP-only when omitted.
				channels: z.array(NotificationChannelSchema).optional(),
			})
		)
	),

	// Notification rules (comprehensive rule engine for event lifecycle notifications)
	notificationRules: z.preprocess(
		(val) => {
			if (!Array.isArray(val)) {
				return [];
			}
			return val;
		},
		z
			.array(
				z.object({
					id: z.string().min(1),
					name: z.string().min(1).max(100),
					enabled: z.boolean(),
					options: NotificationRuleOptionsSchema,
					templateName: z.string(),
					language: z.string(),
					variableMapping: z.record(z.string(), z.string()),
					eventTypeFilter: z.array(z.string()).optional(),
					targetPhones: z.array(z.string()).optional(),
					// Absolute ISO 8601 fire time (the CUSTOM rule's payload; overrides
					// relative timing in `options` when set).
					scheduledAt: z.string().optional(),
					// Delivery channels; DB defaults to WHATSAPP-only when omitted.
					channels: z.array(NotificationChannelSchema).optional(),
				})
			)
			.default([])
	),
});

export type AppConfig = z.infer<typeof AppConfigSchema>;

// ============================================================================
// STRICT BOUNDARY SCHEMA - IMPORT/EXPORT ONLY
// ============================================================================
//
// AppConfigSchema (above) still carries runtime defaults/preprocessors used by
// normal app config parsing. That tolerance is exactly what we do NOT want at
// the import/export boundary: a user-supplied JSON must contain every field
// explicitly, or the import should fail loudly.
//
// AppConfigStrictSchema overrides every field in AppConfigSchema that has
// a silent default (`.default()` or a preprocess that fills missing values)
// with a plain required variant. Export imports accept only the current
// version; older versions must be re-exported from a current instance.

const NotificationRuleItemSchema = z.object({
	id: z.string().min(1),
	name: z.string().min(1).max(100),
	enabled: z.boolean(),
	options: NotificationRuleOptionsSchema,
	templateName: z.string(),
	language: z.string(),
	variableMapping: z.record(z.string(), z.string()),
	eventTypeFilter: z.array(z.string()).optional(),
	targetPhones: z.array(z.string()).optional(),
	// Carried through import/export so CUSTOM rules and multi-channel targeting
	// round-trip (kept optional: the DB defaults both).
	scheduledAt: z.string().optional(),
	channels: z.array(NotificationChannelSchema).optional(),
});

const ReminderTemplateItemSchema = z.object({
	name: z
		.string()
		.min(1)
		.max(50)
		.regex(/^[\w-]+$/),
	templateName: z.string(),
	language: z.string(),
	variableMapping: z.record(z.string(), z.string()),
	sendTime: z.string().regex(/^\d{2}:\d{2}$/),
	messageBody: z.string().optional(),
	channels: z.array(NotificationChannelSchema).optional(),
});

export const AppConfigStrictSchema = AppConfigSchema.extend({
	// Silent .default(true) → now required
	requirePhoneForEvents: z.boolean(),
	requireNameForEvents: z.boolean(),
	businessGoogleMapsUrl: z.url().nullable(),
	businessLandmark: z.string().nullable(),
	businessFloorUnit: z.string().nullable(),
	agentMediaCapabilities: AgentMediaCapabilitiesSchema,
	// Silent .default(true) → now required
	aiAssistantReceiveImages: z.boolean(),
	aiAssistantReceiveAudio: z.boolean(),
	aiAssistantSendImages: z.boolean(),
	aiAssistantSendAudio: z.boolean(),
	systemAgentReceiveImages: z.boolean(),
	systemAgentReceiveAudio: z.boolean(),
	systemAgentSendImages: z.boolean(),
	systemAgentSendAudio: z.boolean(),
	systemAgentEnabled: z.boolean(),
	schedulingToolsEnabled: z.boolean(),
	ragToolsEnabled: z.boolean(),
	availableCalendarLocales: z.array(z.enum(CALENDAR_SYSTEM_VALUES)),
	// Silent .default(...) on the multi-agent fields → now required (plain).
	ragAgentMode: z.enum(RAG_AGENT_MODE_VALUES),
	researcherModel: z.string().nullable(),
	verifierModel: z.string().nullable(),
	verifierMaxLoops: z.number().int().min(1).max(3),
	documentSummaryModel: z.string().nullable(),
	// Silent .default(...) → now required (plain object)
	sensitiveDataGuard: SensitiveDataGuardSchema,
	// Preprocess '' | undefined | null → [] → now required, plain array
	reminderTemplateConfig: z.array(ReminderTemplateItemSchema),
	// Preprocess non-array → [] and .default([]) → now required, plain array
	notificationRules: z.array(NotificationRuleItemSchema),
	waitlistEnabled: z.boolean(),
	waitlistOfferBatchSize: z.number().int().min(1).max(10),
	waitlistOfferTtlMinutes: z.number().int().min(5).max(1440),
	// Silent .default([]) → now required, plain array
	aiPromptPresets: z.array(AiPromptPresetSchema).max(50),
});

// ============================================================================
// TYPE-SAFE FIELD LIST - COMPILE-TIME VERIFIED
// ============================================================================

/**
 * Complete list of all AppConfig field names.
 * This is used for compile-time verification that all fields are mapped.
 * If a new field is added to AppConfigSchema, TypeScript will error here.
 */
const APP_CONFIG_FIELDS = [
	"workingDays",
	"defaultWorkingHours",
	"daySpecificHours",
	"slotDurationHours",
	"calendarMaxOccupancy",
	"daySpecificSlotDurations",
	"customCalendarRanges",
	"calendarColumns",
	"documentsColumns",
	"defaultCountryPrefix",
	"availableLanguages",
	"availableCalendarViews",
	"timezone",
	"llmProvider",
	"llmModel",
	"llmReasoningEffort",
	"llmVerbosity",
	"llmModelFallback",
	"llmFallbackReasoningEffort",
	"llmFallbackVerbosity",
	"llmFallbackMaxOutputTokens",
	"streamReasoning",
	"agentMediaCapabilities",
	"aiAssistantReceiveImages",
	"aiAssistantReceiveAudio",
	"aiAssistantSendImages",
	"aiAssistantSendAudio",
	"systemAgentReceiveImages",
	"systemAgentReceiveAudio",
	"systemAgentSendImages",
	"systemAgentSendAudio",
	"systemAgentEnabled",
	"schedulingToolsEnabled",
	"ragToolsEnabled",
	"llmContextLimit",
	"llmMaxOutputTokens",
	"calendarFirstDay",
	"eventTimeFormat",
	"defaultCalendarView",
	"availableCalendarLocales",
	"calendarDirection",
	"notificationPreferences",
	"waitlistEnabled",
	"waitlistOfferBatchSize",
	"waitlistOfferTtlMinutes",
	"eventTypes",
	"eventColors",
	// Note: vacationPeriods are handled by the calendar, not config
	// Note: systemSettings was removed - timezone is top-level and locale is covered by availableCalendarLocales
	"systemPrompt",
	"systemAgentPrompt",
	"aiPromptPresets",
	"businessName",
	"businessAddress",
	"businessGoogleMapsUrl",
	"businessLandmark",
	"businessFloorUnit",
	"businessLatitude",
	"businessLongitude",
	"tools",
	"ragSettings",
	"ragAgentMode",
	"researcherModel",
	"verifierModel",
	"verifierMaxLoops",
	"documentSummaryModel",
	"sensitiveDataGuard",
	"requirePhoneForEvents",
	"requireNameForEvents",
	"reminderTemplateConfig",
	"notificationRules",
] as const;

/**
 * Type derived from the field list - must match AppConfig keys exactly
 * If they don't match, TypeScript will error
 */
type AppConfigFieldList = (typeof APP_CONFIG_FIELDS)[number];

/**
 * Compile-time assertion: Ensure APP_CONFIG_FIELDS matches AppConfig keys
 * This will cause a compile error if:
 * - A field is in AppConfig but not in APP_CONFIG_FIELDS
 * - A field is in APP_CONFIG_FIELDS but not in AppConfig
 */
type AssertFieldListComplete = AppConfigFieldList extends keyof AppConfig
	? keyof AppConfig extends AppConfigFieldList
		? true
		: never // Error: APP_CONFIG_FIELDS is missing a key from AppConfig
	: never; // Error: APP_CONFIG_FIELDS has an extra key not in AppConfig

// This line verifies the assertion at compile time
true satisfies AssertFieldListComplete;

// ============================================================================
// EXPORT SCHEMA - CURRENT FORMAT ONLY
// ============================================================================
//
// The import/export boundary accepts exactly the current wrapper around
// AppConfigStrictSchema. Legacy direct configs, snake_case DTO exports, and
// older versioned exports are deliberately rejected so missing fields cannot
// be filled silently.

/**
 * Current export format version.
 */
export const CURRENT_EXPORT_VERSION = 5 as const;

/**
 * Current export format — the ONLY format produced by createConfigExport().
 * Validates against AppConfigStrictSchema, which refuses to accept
 * missing fields silently.
 */
export const ConfigExportSchemaV5 = z.object({
	_version: z.literal(5),
	_exportedAt: z.string(),
	_config: AppConfigStrictSchema,
});

export type ConfigExportV5 = z.infer<typeof ConfigExportSchemaV5>;

// ============================================================================
// TRANSFORMATION FUNCTIONS - TYPE-SAFE WITH RUNTIME VALIDATION
// ============================================================================

/**
 * Validate that an object has ALL required AppConfig fields
 * Fails loudly if any field is missing
 */
export function validateAppConfig(data: unknown, context: string): AppConfig {
	const result = AppConfigSchema.safeParse(data);
	if (!result.success) {
		const errors = result.error.issues
			.map((issue) => `  ${issue.path.join(".")}: ${issue.message}`)
			.join("\n");
		throw new Error(
			`[${context}] Config validation failed:\n${errors}\n\nReceived data keys: ${
				data && typeof data === "object" ? Object.keys(data).join(", ") : "N/A"
			}`
		);
	}
	return result.data;
}

/**
 * Validate against the strict boundary schema — refuses configs that
 * omit any importable field. Used by import/export paths only.
 */
function validateAppConfigStrict(data: unknown, context: string): AppConfig {
	const result = AppConfigStrictSchema.safeParse(data);
	if (!result.success) {
		const errors = result.error.issues
			.map((issue) => `  ${issue.path.join(".")}: ${issue.message}`)
			.join("\n");
		throw new Error(
			`[${context}] Strict config validation failed:\n${errors}\n\nReceived data keys: ${
				data && typeof data === "object" ? Object.keys(data).join(", ") : "N/A"
			}`
		);
	}
	return result.data;
}

/**
 * Create an export payload from AppConfig.
 *
 * Always emits the current export version (v5) and validates against
 * AppConfigStrictSchema, which makes it a compile/runtime error to ship
 * a config with any importable field missing. If this ever throws, the
 * live config in memory has drifted from the schema and that is the bug
 * to fix — not a reason to relax export validation.
 */
export function createConfigExport(
	config: AppConfig,
	context: string
): ConfigExportV5 {
	// Strict validation first — guarantees exports are round-trip-safe
	// against the strict import schema.
	const validated = validateAppConfigStrict(config, `${context}:export`);

	const payload: ConfigExportV5 = {
		_version: CURRENT_EXPORT_VERSION,
		_exportedAt: new Date().toISOString(),
		_config: validated,
	};

	const result = ConfigExportSchemaV5.safeParse(payload);
	if (!result.success) {
		const errors = result.error.issues
			.map((issue) => `  ${issue.path.join(".")}: ${issue.message}`)
			.join("\n");
		throw new Error(`[${context}] Export validation failed:\n${errors}`);
	}

	return result.data;
}

/**
 * Result of a config import when the caller opts into ignored-key collection.
 */
export interface ParseConfigImportResult {
	config: AppConfig;
	/**
	 * Top-level keys present on the imported `_config` that the strict schema
	 * does not recognize and therefore strips. Surfaced so the web import layer
	 * can show a "N fields ignored" toast. Empty when the import was clean.
	 */
	ignoredKeys: string[];
}

/**
 * Collect the top-level keys on a raw imported config that are NOT part of the
 * current AppConfig field set (i.e. the ones the strict schema silently strips).
 */
function collectIgnoredConfigKeys(rawConfig: unknown): string[] {
	if (!rawConfig || typeof rawConfig !== "object" || Array.isArray(rawConfig)) {
		return [];
	}
	const known = new Set<string>(APP_CONFIG_FIELDS);
	return Object.keys(rawConfig).filter((key) => !known.has(key));
}

/**
 * Parse an imported config file.
 *
 * Strict, current-version only. Accepts a single shape:
 *   { _version: CURRENT_EXPORT_VERSION, _config: {...}, _exportedAt?: string }
 *
 * Legacy shapes that previous revisions accepted (direct camelCase AppConfig,
 * snake_case DTO format, `version+config` without underscore, or older
 * `_version` values) are deliberately rejected. Users with such a file should
 * re-export from a current instance.
 *
 * Overload: pass `{ collectIgnored: true }` to also receive the list of
 * unknown/removed top-level fields that were stripped, so callers can surface
 * a "N fields ignored" notice. The 2-arg form is unchanged (returns AppConfig)
 * for back-compat with existing callers.
 */
export function parseConfigImport(data: unknown, context: string): AppConfig;
export function parseConfigImport(
	data: unknown,
	context: string,
	options: { collectIgnored: true }
): ParseConfigImportResult;
export function parseConfigImport(
	data: unknown,
	context: string,
	options?: { collectIgnored?: boolean }
): AppConfig | ParseConfigImportResult {
	if (!data || typeof data !== "object") {
		throw new Error(`[${context}] Invalid import data: expected object`);
	}

	const obj = data as Record<string, unknown>;

	if (!("_version" in obj && "_config" in obj)) {
		throw new Error(
			`[${context}] Unsupported import format. Expected { _version, _config, _exportedAt }. ` +
				`Keys found: ${Object.keys(obj).slice(0, 10).join(", ")}. ` +
				"Legacy formats are no longer accepted — re-export the config from a current " +
				`instance to produce a v${CURRENT_EXPORT_VERSION} file.`
		);
	}

	const version = obj._version;
	if (
		typeof version !== "number" ||
		!Number.isInteger(version) ||
		version < 1
	) {
		throw new Error(
			`[${context}] Invalid _version: expected integer >= 1, got ${JSON.stringify(version)}`
		);
	}
	if (version !== CURRENT_EXPORT_VERSION) {
		throw new Error(
			`[${context}] Unsupported export version ${version}. Expected v${CURRENT_EXPORT_VERSION}; ` +
				"re-export the config from a current instance."
		);
	}

	const config = validateAppConfigStrict(
		obj._config,
		`${context}:v${CURRENT_EXPORT_VERSION}`
	);

	if (!options?.collectIgnored) {
		return config;
	}

	return {
		config,
		ignoredKeys: collectIgnoredConfigKeys(obj._config),
	};
}

/**
 * Pick specific fields from AppConfig
 * Type-safe: all field names must be valid AppConfig keys
 */
export function pickConfigFields<K extends keyof AppConfig>(
	config: AppConfig,
	fields: readonly K[]
): Pick<AppConfig, K> {
	const result: Partial<AppConfig> = {};
	for (const field of fields) {
		result[field] = config[field];
	}
	return result as Pick<AppConfig, K>;
}

// ============================================================================
// FIELD INTROSPECTION
// ============================================================================

/**
 * Get all AppConfig field names (camelCase)
 * Useful for iteration and verification
 */
export function getAppConfigFieldNames(): readonly string[] {
	return APP_CONFIG_FIELDS;
}

/**
 * Check if a field name is a valid AppConfig field
 */
export function isAppConfigField(name: string): name is keyof AppConfig {
	return APP_CONFIG_FIELDS.includes(name as AppConfigFieldList);
}
